import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Trash2,
  BookOpen,
  Sparkles,
  ClipboardList,
  Plus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import PageHeader from "@/components/PageHeader";
import SoftButton from "@/components/SoftButton";
import {
  getResults,
  getSavedActions,
  getJournalEntries,
  getMomentCounts,
  logMoment,
  deleteResult,
  deleteSavedAction,
  deleteJournalEntry,
  clearAll,
} from "@/lib/storage";
import { MOMENT_TYPES, MOMENT_ORDER } from "@/lib/momentsData";
import { DASH } from "@/constants/testIds";

const TABS = [
  { id: "results", label: "Reflections", icon: Sparkles, testid: DASH.tabResults },
  { id: "actions", label: "Actions", icon: ClipboardList, testid: DASH.tabActions },
  { id: "entries", label: "Journal", icon: BookOpen, testid: DASH.tabEntries },
];

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export default function Dashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("results");
  const [results, setResults] = useState([]);
  const [actions, setActions] = useState([]);
  const [entries, setEntries] = useState([]);
  const [momentCounts, setMomentCounts] = useState({});
  const [logging, setLogging] = useState(false);

  const refresh = () => {
    setResults(getResults());
    setActions(getSavedActions());
    setEntries(getJournalEntries());
    setMomentCounts(getMomentCounts());
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleLogMoment = (typeId) => {
    logMoment(typeId);
    refresh();
    setLogging(false);
    toast.success("Noted.", {
      description: "A moment you chose to keep.",
    });
  };

  // Honest check-ins is auto-derived from quiz results count.
  const honestCheckIns = results.length;

  const handleClearAll = () => {
    if (
      !window.confirm(
        "This will delete every saved reflection, action, and journal entry from this device. Continue?"
      )
    )
      return;
    clearAll();
    refresh();
    toast.success("Everything cleared.");
  };

  const totalCount = results.length + actions.length + entries.length;

  return (
    <PageContainer testid={DASH.root}>
      <PageHeader
        title="Saved"
        subtitle="Quiet evidence of who you are — beyond the spirals."
        variant="none"
      />

      {/* What you've protected — gentle counters */}
      <section className="mb-7">
        <div className="flex items-center justify-between mb-3 px-1">
          <p
            className="text-[11px] uppercase tracking-[0.28em] font-medium"
            style={{
              color: "var(--un-terracotta)",
              fontFamily: "Manrope, sans-serif",
            }}
          >
            What you've protected
          </p>
          <button
            data-testid={DASH.logMomentBtn}
            onClick={() => setLogging((v) => !v)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium un-press"
            style={{
              background: "rgba(74,32,64,0.06)",
              color: "var(--un-plum)",
              fontFamily: "Manrope, sans-serif",
            }}
          >
            {logging ? <X size={12} /> : <Plus size={12} />}
            {logging ? "Close" : "Log a moment"}
          </button>
        </div>

        <AnimatePresence>
          {logging && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -4 }}
              animate={{ opacity: 1, height: "auto", y: 0 }}
              exit={{ opacity: 0, height: 0, y: -4 }}
              transition={{ duration: 0.28 }}
              className="overflow-hidden mb-3"
            >
              <div
                className="rounded-3xl p-4"
                style={{ background: "var(--un-bg-2)" }}
              >
                <p
                  className="text-[13px] mb-3 italic leading-snug"
                  style={{
                    color: "var(--un-text-2)",
                    fontFamily: "Cormorant Garamond, serif",
                    fontSize: "15px",
                  }}
                >
                  Honor a moment you didn't disappear from. No one sees this but you.
                </p>
                <div className="flex flex-col gap-2">
                  {MOMENT_ORDER.map((mid) => {
                    const m = MOMENT_TYPES[mid];
                    return (
                      <button
                        key={mid}
                        data-testid={`${DASH.logMomentOptionPrefix}${mid}`}
                        onClick={() => handleLogMoment(mid)}
                        className="flex items-center gap-3 text-left px-4 py-3 rounded-2xl un-press"
                        style={{
                          background: "#FDFBF7",
                          boxShadow: "0 4px 12px rgba(74,32,64,0.04)",
                        }}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ background: m.accent }}
                        />
                        <span
                          className="text-[14px] font-medium"
                          style={{
                            color: "var(--un-text-1)",
                            fontFamily: "Manrope, sans-serif",
                          }}
                        >
                          {m.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-2 gap-3">
          {/* Honest check-ins always visible — it's the lightest, most-used signal */}
          <CounterCard
            label="Honest check-ins"
            count={honestCheckIns}
            accent="#5B3954"
            testid={`${DASH.counterPrefix}honest`}
          />
          {MOMENT_ORDER.map((mid) => {
            const m = MOMENT_TYPES[mid];
            const count = momentCounts[mid] || 0;
            // Quieter dashboard: skip empty counters until the user has logged at least one moment of that kind.
            // Keeps the panel from feeling like a productivity scoreboard.
            if (count === 0) return null;
            return (
              <CounterCard
                key={mid}
                label={m.short}
                count={count}
                accent={m.accent}
                testid={`${DASH.counterPrefix}${mid}`}
              />
            );
          })}
        </div>
      </section>

      {/* Tabs */}
      <div
        className="flex p-1.5 rounded-full mb-6"
        style={{ background: "rgba(74,32,64,0.06)" }}
      >
        {TABS.map((t) => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-medium un-press transition-all"
              style={{
                background: isActive ? "#FDFBF7" : "transparent",
                color: isActive ? "var(--un-plum)" : "var(--un-text-3)",
                boxShadow: isActive
                  ? "0 4px 14px rgba(74,32,64,0.10)"
                  : "none",
                fontFamily: "Manrope, sans-serif",
              }}
            >
              <t.icon size={13} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Reflections (quiz results) */}
      {tab === "results" && (
        <>
          {results.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title="No reflections yet"
              ctaLabel="Start Check-In"
              onCta={() => nav("/check-in")}
            />
          ) : (
            <div className="flex flex-col gap-3 mb-10">
              {results.map((r) => {
                const answered = Object.values(r.answers || {}).filter((value) => value !== undefined && value !== null).length;
                return (
                  <motion.div
                    key={r.id}
                    data-testid={`${DASH.itemPrefix}${r.id}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="un-card p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <button
                        onClick={() => nav(`/results/${r.id}`)}
                        className="flex-1 text-left"
                      >
                        <p
                          className="text-[11px] uppercase tracking-[0.25em]"
                          style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
                        >
                          {formatDate(r.createdAt)}
                        </p>
                        <h3
                          className="text-[17px] font-medium leading-snug mt-2"
                          style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
                        >
                          Private Check-In snapshot
                        </h3>
                        <p className="text-[13px] mt-0.5" style={{ color: "var(--un-text-3)" }}>
                          {answered ? `${answered} responses saved` : "Saved reflection"}
                        </p>
                      </button>
                      <button
                        data-testid={`${DASH.deletePrefix}${r.id}`}
                        onClick={() => {
                          deleteResult(r.id);
                          refresh();
                        }}
                        aria-label="Delete"
                        className="w-8 h-8 rounded-full flex items-center justify-center un-press shrink-0"
                        style={{ background: "rgba(117,55,66,0.06)", color: "var(--un-burgundy)" }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Saved actions */}
      {tab === "actions" && (
        <>
          {actions.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="No saved actions yet"
              ctaLabel="Browse Step Forward"
              onCta={() => nav("/step-forward")}
            />
          ) : (
            <div className="flex flex-col gap-3 mb-10">
              {actions.map((a) => (
                <motion.div
                  key={a.id}
                  data-testid={`${DASH.itemPrefix}${a.id}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="un-card p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[11px] uppercase tracking-[0.25em] mb-1.5"
                        style={{
                          color: "var(--un-terracotta)",
                          fontFamily: "Manrope, sans-serif",
                        }}
                      >
                        {a.category}
                      </p>
                      <h3
                        className="text-[17px] font-medium leading-snug mb-1"
                        style={{
                          color: "var(--un-text-1)",
                          fontFamily: "Manrope, sans-serif",
                        }}
                      >
                        {a.title}
                      </h3>
                      <p
                        className="text-[13px] leading-relaxed"
                        style={{ color: "var(--un-text-2)" }}
                      >
                        {a.body}
                      </p>
                    </div>
                    <button
                      data-testid={`${DASH.deletePrefix}${a.id}`}
                      onClick={() => {
                        deleteSavedAction(a.id);
                        refresh();
                      }}
                      aria-label="Remove"
                      className="w-8 h-8 rounded-full flex items-center justify-center un-press shrink-0"
                      style={{
                        background: "rgba(117,55,66,0.06)",
                        color: "var(--un-burgundy)",
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Journal entries */}
      {tab === "entries" && (
        <>
          {entries.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="No journal entries yet"
              ctaLabel="Open Journal"
              onCta={() => nav("/journal")}
            />
          ) : (
            <div className="flex flex-col gap-3 mb-10">
              {entries.map((e) => (
                <motion.div
                  key={e.id}
                  data-testid={`${DASH.itemPrefix}${e.id}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="un-card p-5"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p
                      className="text-[11px] uppercase tracking-[0.25em]"
                      style={{
                        color: "var(--un-text-3)",
                        fontFamily: "Manrope, sans-serif",
                      }}
                    >
                      {formatDate(e.createdAt)}
                    </p>
                    <button
                      data-testid={`${DASH.deletePrefix}${e.id}`}
                      onClick={() => {
                        deleteJournalEntry(e.id);
                        refresh();
                      }}
                      aria-label="Delete"
                      className="w-8 h-8 rounded-full flex items-center justify-center un-press"
                      style={{
                        background: "rgba(117,55,66,0.06)",
                        color: "var(--un-burgundy)",
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {e.prompt && (
                    <p
                      className="italic text-[15px] mb-1"
                      style={{
                        color: "var(--un-burgundy)",
                        fontFamily: "Cormorant Garamond, serif",
                      }}
                    >
                      {e.prompt}
                    </p>
                  )}
                  <p
                    className="text-[14px] leading-relaxed whitespace-pre-wrap line-clamp-5"
                    style={{ color: "var(--un-text-1)" }}
                  >
                    {e.text}
                  </p>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Clear all */}
      {totalCount > 0 && (
        <div className="text-center mb-8">
          <button
            data-testid={DASH.clearAllBtn}
            onClick={handleClearAll}
            className="text-xs font-medium px-4 py-2 rounded-full un-press"
            style={{
              color: "var(--un-burgundy)",
              background: "rgba(117,55,66,0.06)",
              fontFamily: "Manrope, sans-serif",
            }}
          >
            Clear everything on this device
          </button>
        </div>
      )}
    </PageContainer>
  );
}

function EmptyState({ icon: Icon, title, ctaLabel, onCta }) {
  return (
    <div className="text-center py-10 px-4">
      <div
        className="mx-auto w-16 h-16 rounded-full mb-4 flex items-center justify-center"
        style={{ background: "rgba(74,32,64,0.06)" }}
      >
        <Icon size={22} style={{ color: "var(--un-plum)" }} />
      </div>
      <p
        className="text-[17px] font-medium mb-3"
        style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
      >
        {title}
      </p>
      <SoftButton variant="secondary" size="sm" onClick={onCta}>
        {ctaLabel}
      </SoftButton>
    </div>
  );
}

function CounterCard({ label, count, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-3xl p-4 relative overflow-hidden"
      style={{
        background: "#FDFBF7",
        boxShadow: "0 4px 14px rgba(74,32,64,0.04)",
      }}
    >
      <span
        aria-hidden
        className="absolute right-3 top-3 w-1 h-1 rounded-full"
        style={{ background: accent, opacity: 0.7 }}
      />
      <p
        className="text-[26px] font-light leading-none"
        style={{
          color: "var(--un-text-2)",
          fontFamily: "Manrope, sans-serif",
        }}
      >
        {count}
      </p>
      <p
        className="mt-1.5 text-[11px] leading-snug"
        style={{
          color: "var(--un-text-3)",
          fontFamily: "Manrope, sans-serif",
        }}
      >
        {label}
      </p>
    </div>
  );
}
